import React, { useState } from 'react'
import styled from 'styled-components'
import { BigNumber } from 'ethers'
import { MegaButton } from '../../common'
import { useCurrencySelect } from '../../hooks'
import { getButtonState } from './getButtonState'
import { LiquidityState } from './useAddLiquidity'
import { ConfirmAddLiquidity } from './ConfirmAddLiquidity'
import { CurrencyValue, Pair } from '../../model'
import { logAmplitudeEvent } from '../../utils'
import { useAmplitude, useConnectModal } from '../../providers'
import { getVolumeBucket } from '../../utils'

interface Props {
  pair?: Pair
  state: LiquidityState
  currencies: ReturnType<typeof useCurrencySelect>
  firstValue: string
  secondValue: string
  firstUsdValue18?: BigNumber
  secondUsdValue18?: BigNumber
  suggestedDeposit: boolean
  onSubmit: () => void
}

export function AddLiquidityButton({
  pair,
  state,
  currencies,
  firstValue,
  secondValue,
  firstUsdValue18,
  secondUsdValue18,
  suggestedDeposit,
  onSubmit,
}: Props) {
  const [open, setOpen] = useState(false)
  const buttonState = getButtonState(state, currencies.first, currencies.second)
  const { setConnectModalOpen } = useConnectModal()
  const { incrementDepositCount } = useAmplitude()

  async function finalize() {
    onSubmit()
    setOpen(false)
    const volume = firstUsdValue18 && secondUsdValue18 ? firstUsdValue18.add(secondUsdValue18) : null
    logAmplitudeEvent('Deposit: Click Confirm', {
      pair: `${currencies.first.symbol}-${currencies.second.symbol}`,
      volume: volume ? volume.toString() : null,
      volumeBucket: volume ? getVolumeBucket(volume) : null,
      type: suggestedDeposit ? 'suggested' : 'custom',
    })
    incrementDepositCount()
  }

  return (
    <Content>
      <MegaButton
        error={buttonState.error}
        disabled={buttonState.disabled}
        onClick={() => {
          if (state == LiquidityState.WALLET_NOT_CONNECTED) {
            setConnectModalOpen()
            return
          }
          setOpen(true)
          const volume = firstUsdValue18 && secondUsdValue18 ? firstUsdValue18.add(secondUsdValue18) : null
          logAmplitudeEvent('Deposit: Click Add Liquidity', {
            pair: `${currencies.first.symbol}-${currencies.second.symbol}`,
            volume: volume ? volume.toString() : null,
            volumeBucket: volume ? getVolumeBucket(volume) : null,
            type: suggestedDeposit ? 'suggested' : 'custom',
          })
        }}
      >
        {buttonState.text}
      </MegaButton>
      {pair && open && currencies.first && currencies.second && (
        <ConfirmAddLiquidity
          pair={pair}
          firstValue={CurrencyValue.fromString(currencies.first, firstValue)}
          secondValue={CurrencyValue.fromString(currencies.second, secondValue)}
          onSubmit={finalize}
          onClose={() => setOpen(false)}
        />
      )}
    </Content>
  )
}

const Content = styled.div`
  margin-bottom: 20px;
  padding: 0 20px;
`
